import { uploadFile, getFileById } from '@/api/file/fileApi';
import { saveExtractionIntoReport } from '@/api/pdfApi';
import { INIT_NO_OP_NUM, PDF_FILE_TYPE } from '@/config';
import { setPdf, clearPdf, setRotation } from '@/mutation-types';

/** See `storedFileKey` in cda.js: a uid stays a string, never `parseInt`'s leading digits. */
function storedFileKey(value) {
  const text = String(value ?? '').trim();

  if (/^[a-f0-9]{32}$/i.test(text)) return text.toLowerCase();

  return /^\d+$/.test(text) ? Number(text) : INIT_NO_OP_NUM;
}

const state = {
  pdfId: INIT_NO_OP_NUM,    // id in db
  pdfIndex: INIT_NO_OP_NUM, // id in dropdown list
  pdfRotation: 0,           // rotation in degree: 0, 90, 180, 240
};

const getters = {
  getPdfId: state => state.pdfId,
  getPdfIndex: state => state.pdfIndex,
  getPdfRotation: state => state.pdfRotation,

  /**
   * returns a list of all pdf ids
   */
  getPdfList (state, getters, rootState, rootGetters) {
    // pick id and recordedAt from all data
    let pdfList = [];
    const pdfs = rootGetters['login/getAllData']?.pdfs || rootState?.login?.phrData?.pdfs;
    if (Array.isArray(pdfs)) {
      pdfs.forEach((pdf, index) => {
        pdfList.push({
          id: pdf.id,
          index,
          recordedAt: pdf.recordedAt,
          displayName: pdf.displayName || pdf.name || pdf.filename || pdf.recordedAt,
        });
      });
    }
    return pdfList;
  },
  getIndexOfLastPdf (state, getters, rootState, rootGetters) {
    const pdfs = rootGetters['login/getAllData']?.pdfs || rootState?.login?.phrData?.pdfs;
    if (!Array.isArray(pdfs) || pdfs.length === 0) return -1;
    return pdfs.length - 1;
  },
  /** The chart's PDFs could not be read (stores/chartPdfs.js): not the same as it having none. */
  pdfListLoadFailed (state, getters, rootState, rootGetters) {
    return Boolean(rootGetters['login/getAllData']?.pdfsLoadFailed);
  },
};

const mutations = {
  [setPdf] (state, payload) {
    state.pdfId = storedFileKey(payload.pdfId);
    state.pdfIndex = parseInt(payload.pdfIndex);
  },
  [clearPdf] (state) {
    state.pdfId = INIT_NO_OP_NUM;
    state.pdfIndex = INIT_NO_OP_NUM;
  },
  [setRotation] (state, payload) {
    state.pdfRotation = parseInt(payload.pdfRotation);
  },
};

const actions = {

  uploadPdf ({commit, state, rootState}, formData) {
    return new Promise((resolve, reject) => {
      if (formData) {
        uploadFile(PDF_FILE_TYPE, formData)
        .then((response) => {
          const responseData = response.data;
          resolve(responseData.id);
        })
        .catch((err) => {
          reject(err);
        });
      } else {
        reject(`Please include a ${PDF_FILE_TYPE} file (in .xml format) you want to upload `);
      }
    });
  },

  async requestPdfById ({state,commit}, id) {
    return new Promise((resolve, reject) => {
      getFileById(PDF_FILE_TYPE, id)
      .then((response) => {
        resolve(response.pdf);
      })
      .catch((err) => {
        reject(err);
      });
    });
  },

  saveExtraction({commit}, requestParams) {
    return new Promise((resolve, reject) => {
      saveExtractionIntoReport(requestParams)
      .then((response) => {
        // update model
        commit('login/setApplicationModel', response.data, {root: true});
        resolve(response.id);
      })
      .catch((err) => {
        reject(err);
      });
    });
  },

  clearPdf ({commit}) {
    commit(clearPdf);
  },

  setRotation({state, commit}, requestParams) {
    const {id, rotation} = requestParams;
    commit('setRotation', {id: id, pdfRotation: rotation});
  }

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
